import React, { useEffect, useState } from 'react';
import userService from '../../../../../services/user.service';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { useEyeconxStore } from '../../../../../store/eyeconxStore';
import Swal from '../../../../helpers/swal';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Loading from '../../../../common/Loading';
function ClinicUserSettings({ setActiveTab }) {
    const [isDeleting, setIsDeleting] = useState(false);
    const [userDeleted, setUserDeleted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const { clinicId } = useParams();
    const selecteduser = useEyeconxStore((state) => state.selectedUser);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isDirty },
    } = useForm({
        defaultValues: { isAuthenticated: false },
        shouldUnregister: true,
    });
    const [roles, setRoles] = useState([]);

    useEffect(() => {
        if (selecteduser) {
            getUserInfoById();
            getRoles();
        }
    }, [selecteduser]);

    const handleBack = () => {
        setActiveTab('clinic-users-tab');
    };

    const getRoles = () => {
        userService
            .getRoles()
            .then((res) => setRoles(res.data))
            .catch(() => {
                toast.error('Failed to load roles', { position: toast.POSITION.BOTTOM_RIGHT });
            });
    };

    const getUserInfoById = () => {
        setLoading(true);
        userService
            .getUser(selecteduser.clinicId, selecteduser.id)
            .then((res) => {
                setUser(res.data);
                reset({
                    ...res.data,
                    isAuthenticated: res.data.isAuthenticated.toString(),
                });
            })
            .catch(() => {
                toast.error('Failed to load user data', { position: toast.POSITION.BOTTOM_RIGHT });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const deleteUser = async (e) => {
        e.preventDefault();

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This will permanently delete the user!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `<i class="bi bi-check"></i> Delete`,
            cancelButtonText: `<i class="bi bi-x"></i> Cancel`,
            input: 'text',
            inputLabel: 'Type "DELETE" to confirm',
            inputPlaceholder: 'Type DELETE here...',
            inputAttributes: { autocapitalize: 'off' },
            customClass: {
                confirmButton: 'btn btn-danger btn-sm me-2',
                cancelButton: 'btn btn-secondary btn-sm',
                title: 'card-title',
                inputLabel: 'small',
                html: 'small',
            },
            inputValidator: (value) => {
                if (value?.toLowerCase() !== 'delete') {
                    return 'You need to type "DELETE" to confirm';
                }
            },
        });

        if (!result.isConfirmed) return;

        try {
            setIsDeleting(true);
            await userService.deleteUser(user.id, clinicId);
            await Swal.fire('Deleted!', 'User has been deleted.', 'success');
            setUserDeleted(true);
            handleBack();
        } catch (err) {
            await Swal.fire('Error!', err.response?.data?.message || 'Error while deleting user', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const canDeleteUser = () => user && user.role !== 'OTHER';

    const resetPassword = () => {
        setIsResetting(true);
        userService
            .passwordReset(clinicId, user.id)
            .then(() => {
                toast.success('Password reset email sent successfully!', { position: toast.POSITION.BOTTOM_RIGHT });
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || 'Error sending password reset', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            })
            .finally(() => {
                setIsResetting(false);
            });
    };

    const onSubmit = (data) => {
        setIsSubmitting(true);
        let newData = {
            ...data,
            isAuthenticated: data.isAuthenticated === 'true',
        };

        if (!user.isAuthenticated && newData.isAuthenticated) {
            newData = { ...newData, secret: 'ECXuserx1y1z1' };
        }

        userService
            .updateUser(user.id, clinicId, newData)
            .then(() => {
                toast.success('User updated successfully!', { position: toast.POSITION.BOTTOM_RIGHT });
                handleBack();
            })
            .catch((err) => {
                toast.error(err.response?.data?.errorMessage || 'Error updating user', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    if (userDeleted) return null;

    if (!selecteduser) {
        return (
            <div className="notifications-panel">
                <div className="notifications-panel__body">
                    <div className="clinic-users-empty">
                        <i className="bi bi-person"></i>
                        No user selected. Return to the users list to choose a user.
                        <div className="mt-3">
                            <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleBack}>
                                Back to Users
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                <div>
                    <button type="button" className="clinic-users-back-link mb-2" onClick={handleBack}>
                        <i className="bi bi-arrow-left"></i>
                        Back to Users
                    </button>
                    <h5 className="clinic-users__title mb-1">
                        <i className="bi bi-pencil text-primary me-1"></i>
                        Edit User — {selecteduser.firstName} {selecteduser.lastName}
                    </h5>
                    <p className="text-muted small mb-0">Update account details and manage user actions.</p>
                </div>
            </div>

            {loading ? (
                <Loading />
            ) : (
                <div className="row g-3">
                    <div className="col-lg-8">
                        <div className="notifications-panel">
                            <div className="notifications-panel__header">
                                <i className="bi bi-person-lines-fill"></i>
                                User Details
                            </div>
                            <div className="notifications-panel__body">
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label htmlFor="firstName" className="notifications-form-label">
                                                First Name <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="firstName"
                                                className={`form-control form-control-sm ${errors.firstName ? 'is-invalid' : ''}`}
                                                placeholder="Enter first name"
                                                {...register('firstName', {
                                                    required: 'First name is required',
                                                    minLength: { value: 2, message: 'Minimum 2 characters required' },
                                                })}
                                            />
                                            {errors.firstName && <div className="invalid-feedback">{errors.firstName.message}</div>}
                                        </div>

                                        <div className="col-md-6">
                                            <label htmlFor="lastName" className="notifications-form-label">
                                                Last Name <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="lastName"
                                                className={`form-control form-control-sm ${errors.lastName ? 'is-invalid' : ''}`}
                                                placeholder="Enter last name"
                                                {...register('lastName', {
                                                    required: 'Last name is required',
                                                    minLength: { value: 2, message: 'Minimum 2 characters required' },
                                                })}
                                            />
                                            {errors.lastName && <div className="invalid-feedback">{errors.lastName.message}</div>}
                                        </div>

                                        <div className="col-md-6">
                                            <label htmlFor="email" className="notifications-form-label">
                                                Email
                                            </label>
                                            <input
                                                type="email"
                                                id="email"
                                                className={`form-control form-control-sm ${errors.email ? 'is-invalid' : ''}`}
                                                placeholder="Enter email"
                                                {...register('email', {
                                                    pattern: {
                                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                        message: 'Invalid email address',
                                                    },
                                                })}
                                            />
                                            {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
                                        </div>

                                        <div className="col-md-6">
                                            <label htmlFor="username" className="notifications-form-label">
                                                Username <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="username"
                                                className={`form-control form-control-sm ${errors.username ? 'is-invalid' : ''}`}
                                                placeholder="Enter username"
                                                {...register('username', {
                                                    required: 'Username is required',
                                                    minLength: { value: 4, message: 'Minimum 4 characters required' },
                                                })}
                                            />
                                            {errors.username && <div className="invalid-feedback">{errors.username.message}</div>}
                                        </div>

                                        <div className="col-md-6">
                                            <label htmlFor="role" className="notifications-form-label">
                                                Role <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                id="role"
                                                className={`form-select form-select-sm ${errors.role ? 'is-invalid' : ''}`}
                                                {...register('role', { required: 'Role is required' })}
                                            >
                                                <option value="">Select a role</option>
                                                {roles.map((item) => (
                                                    <option key={item} value={item}>
                                                        {item}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.role && <div className="invalid-feedback">{errors.role.message}</div>}
                                        </div>

                                        <div className="col-md-6">
                                            <label htmlFor="isAuthenticated" className="notifications-form-label">
                                                Authentication Status <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                id="isAuthenticated"
                                                className={`form-select form-select-sm ${errors.isAuthenticated ? 'is-invalid' : ''}`}
                                                {...register('isAuthenticated', { required: 'Authentication status is required' })}
                                            >
                                                <option value="false">No</option>
                                                <option value="true">Yes</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="clinic-users-form-actions">
                                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleBack} disabled={isSubmitting}>
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-success btn-sm" disabled={isSubmitting || !isDirty}>
                                            {isSubmitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                    Updating…
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-floppy me-1"></i>
                                                    Update User
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-4 d-flex flex-column gap-3">
                        <div className="notifications-panel">
                            <div className="notifications-panel__header">
                                <i className="bi bi-gear"></i>
                                User Actions
                            </div>
                            <div className="notifications-panel__body d-grid gap-2">
                                {user?.isAuthenticated && (
                                    <button type="button" className="btn btn-outline-success btn-sm" onClick={resetPassword} disabled={isResetting}>
                                        {isResetting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                Sending…
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-envelope-arrow-up me-1"></i>
                                                Send Password Reset
                                            </>
                                        )}
                                    </button>
                                )}

                                {canDeleteUser() ? (
                                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={deleteUser} disabled={isDeleting}>
                                        {isDeleting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                Deleting…
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-trash me-1"></i>
                                                Delete User
                                            </>
                                        )}
                                    </button>
                                ) : (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="delete-user-tooltip">Cannot delete users with OTHER role</Tooltip>}
                                    >
                                        <span className="d-grid">
                                            <button type="button" className="btn btn-outline-danger btn-sm" disabled>
                                                <i className="bi bi-trash me-1"></i>
                                                Delete User
                                                <i className="bi bi-lock ms-1"></i>
                                            </button>
                                        </span>
                                    </OverlayTrigger>
                                )}
                            </div>
                        </div>

                        <div className="notifications-panel">
                            <div className="notifications-panel__header">
                                <i className="bi bi-info-circle"></i>
                                Important Note
                            </div>
                            <div className="notifications-panel__body">
                                <div className="notifications-alert-banner">
                                    <i className="bi bi-shield-lock"></i>
                                    <span>
                                        When enabling authentication, the default password is <code>ECXuserx1y1z1</code>. Use Send Password Reset to
                                        email secure instructions to the user.
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default ClinicUserSettings;
